/**
 * MemberPickerModal - Select available entities to add to a team
 *
 * Shows available entities (where team_id is null), searchable, single-select.
 */
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { X, Search, User, Car, Wrench, Check, RefreshCw } from 'lucide-react';
import { driverAPI, vehicleAPI, toolAPI } from '../../api';

// Les libelles viennent de i18n (teams.picker.<type>.*) : ici, seulement l'icone.
const TYPE_CONFIG = {
  driver: { Icon: User },
  vehicle: { Icon: Car },
  tool: { Icon: Wrench },
};

export default function MemberPickerModal({ memberType, onSelect, onClose }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const config = TYPE_CONFIG[memberType];

  const { data, isLoading } = useQuery({
    queryKey: ['available-members', memberType],
    queryFn: () => {
      switch (memberType) {
        case 'driver': return driverAPI.getAll();
        case 'vehicle': return vehicleAPI.getAll();
        case 'tool': return toolAPI.getAll();
        default: return { data: [] };
      }
    },
  });

  // Filter to only available entities (no team assigned)
  const available = useMemo(() => {
    const all = data?.data || [];
    const unassigned = all.filter(e => !e.team_id);

    if (!search) return unassigned;
    const q = search.toLowerCase();

    return unassigned.filter(entity => {
      switch (memberType) {
        case 'driver':
          return `${entity.first_name} ${entity.last_name}`.toLowerCase().includes(q) ||
                 entity.email?.toLowerCase().includes(q);
        case 'vehicle':
          return `${entity.make} ${entity.model}`.toLowerCase().includes(q) ||
                 entity.license_plate?.toLowerCase().includes(q);
        case 'tool':
          return entity.name?.toLowerCase().includes(q) ||
                 entity.tool_code?.toLowerCase().includes(q);
        default:
          return true;
      }
    });
  }, [data, search, memberType]);

  const renderEntity = (entity) => {
    switch (memberType) {
      case 'driver':
        return {
          primary: `${entity.first_name} ${entity.last_name}`,
          secondary: entity.email || entity.phone || '',
          badge: entity.status,
        };
      case 'vehicle':
        return {
          primary: `${entity.make} ${entity.model}`,
          secondary: entity.license_plate || entity.vin || '',
          badge: entity.status,
        };
      case 'tool':
        return {
          primary: entity.name,
          secondary: entity.tool_code || entity.serial_number || '',
          badge: entity.condition,
        };
      default:
        return { primary: `#${entity.id}`, secondary: '', badge: '' };
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <config.Icon className="h-5 w-5 text-indigo-600" />
            <h3 className="text-base font-bold text-gray-900">
              {t(`teams.picker.${memberType}.title`)}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={t(`teams.picker.${memberType}.searchPlaceholder`)}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              autoFocus
            />
          </div>
          <p className="text-xs text-gray-500 mt-1.5">
            {t(`teams.picker.${memberType}.available`, { count: available.length })}
          </p>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-5 w-5 animate-spin text-indigo-500" />
            </div>
          ) : available.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <config.Icon className="h-8 w-8 mx-auto text-gray-300 mb-2" />
              <p className="text-sm">{t(`teams.picker.${memberType}.empty`)}</p>
              <p className="text-xs mt-1">{t('teams.picker.emptyHint')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {available.map(entity => {
                const display = renderEntity(entity);
                return (
                  <button
                    key={entity.id}
                    onClick={() => onSelect(entity.id)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-indigo-50 transition-colors text-left"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{display.primary}</p>
                      {display.secondary && (
                        <p className="text-xs text-gray-500 truncate">{display.secondary}</p>
                      )}
                    </div>
                    {display.badge && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize ml-2 flex-shrink-0">
                        {display.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            {t('common.actions.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
